import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { Location } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, Signal, signal, TemplateRef, ViewChild, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatDrawer } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { AiMessagesUtil, AiResponses, AiUtility, StorageHelper } from 'countable@helpers';
import { AdjustmentObj, AdjustmentRowModel, AI_ALLOWED_EXT, AI_KEYS, AiDownloadModel, AIInputModel, AIResModel, AiStateModel, AmortizationInputsModel, AmortizationScheduleModel, ChangeEngModel, ChatActionModel, ChatLucaMsgModel, ChatSavingInputModel, ChatsModel, ChatUserMsgModel, CommonInputModel, DialogResizeModel, GLSelectionModel, GLTbYearModel, LucaClosedModel, LucaDialogInputModel, LucaEngListModel, LucaFileModel, LucaInputsModel, pageInfo, PROMPT_MSG_TYPES, PROMPTS, ReconInfoModel, ResponseActionModel, SaveChatMsgModel, SavedChatHistoryModel, SaveExcelModel, ScheduleAdjustmentModel, SuggestedPromptsModel, UserDetailModel, VOICE_INPUT, VOICE_PROGRESS, VOICE_SEND, VOICE_SEND_INPUT } from 'countable@model';
import { AiService } from 'countable@services';
import { saveAs } from 'file-saver';
import { KatexOptions } from 'ngx-markdown';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Observable, of, Subject, Subscription, switchMap, takeUntil } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { ContextResModel, freeContextOutputModel } from 'src/app/model/ai/context.model';
import { ResizeModel } from 'src/app/model/eng/resize.model';
import { PlaidService } from 'src/app/services/plaid.service';
import { AiPromptsUtil } from '../../../helpers/ai-prompts.util';
import { ApiService } from '../../../services/api.service';
import { EngService } from '../../../services/eng.service';
import { VoiceRecognitionService } from '../../../store/voice-recogntion.service';

@Component({
  selector: 'app-ai-preview',
  templateUrl: './ai-preview.component.html',
  styleUrl: './ai-preview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AiPreviewComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() set dialogState(data: LucaDialogInputModel) {
    if (data) {
      this.dialogInfo.set(structuredClone(data));
      this.setDialogState();
    }
  }

  @Input() set windowState(stateInfo: AiStateModel) {
    if (stateInfo) {
      this.setWindowState(stateInfo);
    }
  }

  @Output() triggerAction: EventEmitter<Partial<LucaClosedModel>> = new EventEmitter<Partial<LucaClosedModel>>(true);

  @ViewChild('autosize') autoSize: CdkTextareaAutosize;
  @ViewChild('textField') textArea: HTMLTextAreaElement;
  @ViewChild('sessionManagement') sessionManagement: TemplateRef<any>;
  @ViewChild('refreshSession') refreshSession: TemplateRef<any>;
  @ViewChild('listMenu') listMenu: MatMenuTrigger;
  @ViewChild('mainChatWindowEle') private mainChatWindowEle: ElementRef;
  @ViewChild('fileInput') fileInput: ElementRef;
  @ViewChild('endOfPage') endOfPage!: ElementRef;
  @ViewChild('drawer') drawer: MatDrawer;

  public aiUtil: typeof AiUtility = AiUtility;
  public aiResponses: typeof AiResponses = AiResponses;
  public aiPrompt: typeof AiPromptsUtil = AiPromptsUtil;

  public userInput: FormControl<string | null> = new FormControl<string | null>({
    value: '',
    disabled: true
  });
  public searchText: FormControl<string | null> = new FormControl<string | null>({
    value: '',
    disabled: true
  });
  recognizing: any;
  public dialogInfo: WritableSignal<LucaDialogInputModel> = signal<LucaDialogInputModel>(null);
  public userDetails: WritableSignal<UserDetailModel> = signal<UserDetailModel>(null);
  public chatHistory: WritableSignal<SavedChatHistoryModel> = signal<SavedChatHistoryModel>(null);
  public contextRes: WritableSignal<ContextResModel> = signal<ContextResModel>(null);
  public suggestedPrompts: WritableSignal<SuggestedPromptsModel[]> = signal<SuggestedPromptsModel[]>([]);
  public selectedPrompt: WritableSignal<SuggestedPromptsModel> = signal<SuggestedPromptsModel>(null);
  private context: WritableSignal<any | null> = signal<any | null>(null);
  public filePath: WritableSignal<string | null> = signal<string | null>(null);
  public selectedEng: WritableSignal<LucaEngListModel> = signal<LucaEngListModel>(null);
  public isFromHistory: WritableSignal<boolean> = signal<boolean>(false);
  public subscription: Subscription = new Subscription();
  public uploadedFiles: File[] | null = null;
  public engagementsList: WritableSignal<LucaEngListModel[]> = signal<LucaEngListModel[]>([]);
  public originalEngList: WritableSignal<LucaEngListModel[]> = signal<LucaEngListModel[]>([]);
  public hasNoEngagements: WritableSignal<boolean> = signal<boolean>(false);
  public isFetchingChat: WritableSignal<boolean> = signal<boolean>(false);
  public hasNoSelection: Signal<boolean> = computed(() => {
    return !this.selectedEng() || this.selectedEng()?.engId === 0;
  });
  public showPrompts: Signal<boolean> = computed(() => {
    if ((this.originalEngList()?.length === 0) || this.isFetchingChat() || (this.dialogInfo()?.isAiRestricted)
      || (this.selectedEng()?.engId > 0 && this.selectedEng()?.isRestricted)) {
      return false;
    }
    if ((this.chatHistory()?.promptId > 0) || (this.chatHistory()?.messages?.length >= 2)) {
      return false;
    }
    return true;
  });
  public searchEngText: WritableSignal<string> = signal<string>('');
  public hasRefresh: WritableSignal<boolean> = signal<boolean>(false);
  public inputImgPath: WritableSignal<AIInputModel> = signal<AIInputModel>(null);
  public selectedChat: WritableSignal<ChatsModel> = signal<ChatsModel>(null);
  public expandState: WritableSignal<DialogResizeModel> = signal<DialogResizeModel>(null);
  public isUserSpeaking: boolean = false;
  public openDocRepo: WritableSignal<boolean> = signal<boolean>(false);
  public openContextWindow: WritableSignal<boolean> = signal<boolean>(false);
  public chatsList: WritableSignal<ChatsModel[]> = signal<ChatsModel[]>([]);
  public updateChat: WritableSignal<ChatsModel> = signal<ChatsModel>(null);
  public hasCopiedText: WritableSignal<number> = signal<number>(1000);
  public download$: Subject<AiDownloadModel> = new Subject<AiDownloadModel>();
  public isWindowMode: WritableSignal<boolean> = signal<boolean>(false);
  public SH: typeof StorageHelper = StorageHelper;
  public readonly AK: typeof AI_KEYS = AI_KEYS;
  public readonly AiMsg: typeof AiMessagesUtil = AiMessagesUtil;
  public actionMessage: WritableSignal<ResponseActionModel> = signal<ResponseActionModel>(null);
  public isShownFreeContextArrow: WritableSignal<number> = signal<number>(0);
  public index: number = 1000;
  private cancelRequest$: Subject<void> = new Subject<void>();
  public isInEngagement: WritableSignal<boolean> = signal<boolean>(false);
  public hasdraftInputs: WritableSignal<boolean> = signal<boolean>(false);
  public draftPromptInputs: WritableSignal<Partial<LucaInputsModel>> = signal<Partial<LucaInputsModel>>(null);
  public showGoToTop: WritableSignal<boolean> = signal<boolean>(false);
  private debounceTimer: any;
  public isVoiceChatDisabled: WritableSignal<boolean> = signal<boolean>(false);
  private currentMessageObj: ChatLucaMsgModel | null = null;
  public hasPromptWorkFlow: Signal<boolean> = computed(() => {
    const message = this.chatHistory()?.messages?.length > 0 ? this.chatHistory()?.messages[this.chatHistory()?.messages?.length - 1] : null;
    return (message && (message?.messageType === 'WORKFLOW' && !['CAAS_SCHEDULE', 'GENERAL_LEDGER'].includes(message?.specialPromptMessage)) ||
      (message?.messageType === 'SYSTEM_AI' && !!message?.showSpinner)
    );
  });
  public attachedFiles: WritableSignal<LucaFileModel[]> = signal<LucaFileModel[]>([]);
  public readonly allowedExt: WritableSignal<string[]> = signal<string[]>(AI_ALLOWED_EXT);
  private destroyRef = inject(DestroyRef);
  public hasInValidFiles: Signal<boolean> = computed(() => {
    return this.attachedFiles()?.some(a => !this.allowedExt()?.includes(a?.extension)) ||
      this.attachedFiles()?.length > 15;
  });
  public katexInfo: WritableSignal<KatexOptions> = signal<KatexOptions>(null);
  private destroyVoice$: Subject<any> = new Subject<any>();
  public promptInputFiles$: Observable<File[]> = this.chatService.attachedFiles.pipe(
    map(filesMap => {
      return filesMap ? Array.from(filesMap.values()) : [];
    })
  );
  public isContextLoading: WritableSignal<boolean> = signal<boolean>(true);
  public engList: Signal<any> = toSignal(this.engService.refreshSpecificEng$);
  public reconInfo: WritableSignal<Partial<ReconInfoModel>> = signal<Partial<ReconInfoModel>>(null);

  constructor(private apiService: ApiService, private voice2Txt: VoiceRecognitionService, private router: Router,
              private loc: Location, private cdr: ChangeDetectorRef, public toaster: ToastrService,
              private plaidService: PlaidService,
              private dialog: MatDialog, private chatService: AiService, private engService: EngService) {
    this.updateStorageValues();
    this.userDetails.set(JSON.parse((localStorage.getItem('userDetails'))));
    this.inputImgPath.set(structuredClone(VOICE_INPUT));

    this.katexInfo.set({
      displayMode: false,
      delimiters: [
        {left: '$$', right: '$$', display: true},
        {left: '$', right: '$', display: false},
        {left: '\\(', right: '\\)', display: false},
        {left: '\\[', right: '\\]', display: true},
        {left: '\\begin{equation}', right: '\\end{equation}', display: true},
        {left: '\\begin{align}', right: '\\end{align}', display: true}
      ],
      throwOnError : false
    })
  }

  ngOnInit() {
    this.setDefaultState('INIT');

    this.searchText.valueChanges.subscribe({
      next: res => {
        this.searchEngText.set(res);
      }
    });

    this.download$.pipe().subscribe({
      next: res => {
        if (res && res?.isSaveToEngagement === false) {
          this.downloadAiResponse(res);
        }
        if (res && res?.isSaveToEngagement) {
          this.saveToEngagement(res);
        }
      }
    });
  }

  ngAfterViewInit() {
    this.updateButtonVisibility();
    this.initVoiceInput();
  }

  private setDialogState() {
    if (this.dialogInfo()?.hasNoEngagements && !(this.dialogInfo()?.engList?.length > 0)) {
      this.disablePreview();
    } else if (!this.dialogInfo()?.hasNoEngagements && !this.dialogInfo()?.isMinimizedState) {
      this.searchText.enable();
      this.engagementsList.set(this.dialogInfo()?.engList);
      this.originalEngList.set(this.dialogInfo()?.engList);
      this.updatePageInformation(this.dialogInfo()?.pageInfo);
    } else if (!this.dialogInfo()?.hasNoEngagements && this.dialogInfo()?.isMinimizedState) {
      this.updateMinimizeState(structuredClone(this.dialogInfo()?.dialogInfo));
    }

    if (this.dialogInfo()?.isAiRestricted) {
      this.userInput.disable();
      this.inputImgPath().isDisabled = true;
    }
  }

  private setWindowState(stateInfo: AiStateModel) {
    if (stateInfo?.hasNoEngagements) {
      this.disablePreview();
      return;
    }
    this.searchText.enable();
    this.engagementsList.set(stateInfo?.engList);
    this.originalEngList.set(stateInfo?.engList);
    this.chatsList.set(stateInfo?.chatsList);
    this.selectedChat.set(stateInfo?.activeChat);
    if (stateInfo?.hasSelectedEng) {
      this.userInput.enable();
      this.selectedEng.set(stateInfo?.selectedEng);
      this.searchText.setValue(this.selectedEng()?.engName, {emitEvent: false, onlySelf: true});
      this.searchEngText.set(this.selectedEng()?.engName);
      this.setPromptByEngType(this.selectedEng()?.engTypeId);
      this.getContextDetails();
    }
    if (stateInfo?.hasSelectedEng && stateInfo?.hasActiveChat) {
      this.navigateToHistory(stateInfo?.activeChat);
    } else if (stateInfo?.hasSelectedEng && !stateInfo?.hasActiveChat) {
      this.chatHistory.set(stateInfo?.chatHistory);
    } else if (!stateInfo?.hasSelectedEng) {
      this.updatePageInformation(stateInfo?.pageInfo);
    }
  }

  private disablePreview() {
    this.hasNoEngagements.set(true);
    this.searchText.setValue('');
    this.userInput?.setValue('');
    this.searchText?.disable();
    this.userInput?.disable();
    this.chatService.resetValues();
  }

  public askGpt() {
    if (this.userInput?.disabled || (!this.userInput?.value?.trim() && this.attachedFiles()?.length === 0)) {
      return;
    }
    const updateHistory = this.aiUtil?.getChatApiObj(structuredClone(this.chatHistory()));
    const hasUploadedFiles: boolean = this.attachedFiles()?.length > 0;
    const input: FormData = new FormData();
    input.append('query', this.userInput.getRawValue());
    input.append('context', this.getPromptContext() ?? '');
    updateHistory?.forEach(h => (delete h.isShowSpinner));
    input.append('history', JSON.stringify(updateHistory));
    input.append('chat_object', JSON.stringify(this.getChatSavingObj('Free Prompting', this.userInput.getRawValue())));
    this.uploadedFiles?.forEach(f => {
      input.append('files', f);
    });

    const prompt = this.aiUtil?.getUserChatMsg('USER', this.userInput.getRawValue(), this.attachedFiles()?.map(f => f?.fileName) ?? []);
    this.setHistoryByChatMsg(prompt, 'PUSH');
    const resPrompt = this.aiUtil?.getAiChatMsg('SYSTEM_PROMPT_RES_LOADING', this.aiResponses?.loadingContent);
    this.setHistoryByChatMsg(resPrompt, 'PUSH');
    this.setQueryProgressState('PROGRESS');
    this.attachedFiles.set([]);
    this.uploadedFiles = null;
    this.chatService.attachedFiles.next(null);
    if (!(this.chatHistory()?.promptId > 0)) {
      this.chatHistory.update(chat => {
        chat.promptId = 10;
        return chat;
      });
    }
    this.chatService.fetchGptResponse(input).pipe(
      takeUntil(this.cancelRequest$),
      debounceTime(20),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
    ).subscribe({
      next: res => {
        if (res) {
          this.processChunkedResponse(res,hasUploadedFiles);
        }
      }, error: err => {
        this.setHistoryByChatMsg(this.aiUtil?.getAiChatMsg('SYSTEM_PROMPT_RES_OUTPUT', this.aiResponses?.resError), 'REMOVE');
      },
      complete: () => {
      }
    }).add(() => {
      this.setQueryProgressState('DONE');
    });
  }

  private processChunkedResponse(jsonChunk: any,hasUploadedFiles: boolean = false) {
    try {
      if (jsonChunk && typeof (jsonChunk) === 'string') {
        const chunkMessage = jsonChunk;
        if (!this.currentMessageObj) {
          this.currentMessageObj = this.aiUtil.getAiChatMsg('SYSTEM_PROMPT_RES_LOADING', chunkMessage);
          this.currentMessageObj.isLucaStreaming = true;
        } else {
          this.currentMessageObj = this.aiUtil.getAiChatMsg('SYSTEM_PROMPT_RES_OUTPUT', chunkMessage);
          this.currentMessageObj.isLucaStreaming = true;
          this.updateLastMessageInHistory(this.currentMessageObj, true);
        }
      }
      if (jsonChunk?.chat_object?.status === 200 && !!jsonChunk?.chat_object?.data) {
        if (this.currentMessageObj) {
          this.currentMessageObj = this.aiUtil.getAiChatMsg('SYSTEM_PROMPT_RES_OUTPUT', this.currentMessageObj.lucaResponse);
          this.currentMessageObj.showSpinner = false;
          this.updateLastMessageInHistory(this.currentMessageObj,false);
        } else {
          this.currentMessageObj = this.aiUtil.getAiChatMsg('SYSTEM_PROMPT_RES_OUTPUT', jsonChunk?.data[0]?.lucaResponse);
          this.updateLastMessageInHistory(this.currentMessageObj);
        }
        hasUploadedFiles && this.updateQueryWithFileData(jsonChunk?.data[0]?.query_with_file_data);
        this.updateChatInformation(jsonChunk.chat_object.data);
        this.currentMessageObj = null;
      }
    } catch (error) {
    }
  }

  private updateLastMessageInHistory(updatedMessage: ChatLucaMsgModel, hasStreaming: boolean = false) {
    updatedMessage.isLucaStreaming = hasStreaming;
    this.chatHistory.update((chat) => {
      chat.messages[chat.messages.length - 1] = updatedMessage;
      return {...chat} as SavedChatHistoryModel;
    });
    this.cdr.markForCheck();
  }

  private updateQueryWithFileData(fileInfo: string = '') {
    this.chatHistory.update((chat) => {
      const msgs = [...chat.messages];
      if (msgs.length < 2) {
        return chat;
      }
      const secondLastIndex = msgs.length - 2;
      const targetMsg = msgs[secondLastIndex] as ChatUserMsgModel;
      targetMsg.queryWithFileData = fileInfo;

      msgs[secondLastIndex] = {...targetMsg} as ChatUserMsgModel;
      return {...chat,messages: [...msgs]} as SavedChatHistoryModel;
    });
  }

  public handleUserInput(type: 'SEND' | 'VOICE_START' | 'CHAT_STOP' | 'VOICE_PROGRESS' | 'USER_INPUT' | 'NONE') {
    switch (type) {
      case 'VOICE_START':
        this.inputImgPath.set(structuredClone(VOICE_PROGRESS));
        this.userInput.disable();
        this.startRecording();
        break;
      case 'VOICE_PROGRESS':
        this.inputImgPath.set(structuredClone(VOICE_SEND));
        this.userInput.enable();
        this.voice2Txt.stop();
        break;
      case 'SEND':
        this.stopRecording();
        this.inputImgPath.set(structuredClone(VOICE_INPUT));
        this.askGpt();
        break;
      case 'USER_INPUT':
        const userInput = this.userInput.getRawValue();
        if (userInput && userInput?.trim()?.length > 0) {
          this.inputImgPath.set(structuredClone(VOICE_SEND_INPUT));
          this.inputImgPath().isDisabled = false;
        } else {
          this.inputImgPath.set(structuredClone(VOICE_INPUT));
        }
        break;
    }
  }

  public setWorkflow(prompt: SuggestedPromptsModel) {
    const aiChat = this.aiUtil?.getAiChatMsg((prompt?.id === 3 || prompt?.id === 6 || prompt?.id === 7)
        ? 'SYSTEM_PROMPT_RES_LOADING' : 'WORKFLOW',
      (prompt?.id === 6) ? this.getLoadingContent() : prompt?.prompt);
    aiChat.specialPromptMessage = prompt?.promptType;
    if (prompt?.id === 4 || prompt?.id === 5 || prompt?.id === 9 || prompt?.id === 7) {
      this.chatService.attachedFiles.next(null);
      aiChat.specialPromptData = this.aiPrompt.getFileUploadObj(prompt?.promptType);
    }
    if (prompt?.id === 1) {
      aiChat.specialPromptData = this.aiPrompt.getVRInputs();
    }
    const userChat = this.aiUtil?.getUserChatMsg('USER', `Generate ${prompt?.prompt}`);
    const chatInfo = structuredClone(this.chatHistory());
    chatInfo.promptId = prompt?.id;
    chatInfo.messages = (chatInfo && (chatInfo?.messages?.length > 0)) ? [...chatInfo?.messages, userChat, aiChat] : [userChat, aiChat];
    this.chatHistory.set(structuredClone(chatInfo));
    this.selectedPrompt.set(prompt);
    switch (prompt?.id) {
      case 1:
        this.getVr();
        break;
      case 2:
        this.getAr();
        break;
      case 3:
        this.getGLForTbAccounts();
        break;
      case 4:
      case 5:
        break;
      case 6:
        this.getAmortizationInputs();
        break;
      case 7:
        this.getTbAccounts('RECONCILIATION')
        break;
      case 9:
        this.getTbAccounts('LAS');
        break;
    }
  }

  public getPromptReport(input: CommonInputModel, chat: SavedChatHistoryModel, hasLoadingState: boolean = false) {
    const formData: FormData = new FormData();
    formData.append('prompt_type', input.type);
    formData.append('chat_object', JSON.stringify(input?.chat_object));
    delete input?.chat_object;
    formData.append('data', JSON.stringify(input));
    const hasMarkdown: boolean = input?.file_paths?.length > 0;
    !hasLoadingState && this.setLoadingState();
    this.setQueryProgressState('PROGRESS');
    this.chatService.fetchPromptReport(formData, hasMarkdown).pipe(
      takeUntil(this.cancelRequest$)
    ).subscribe({
      next: res => {
        if (res?.data?.status === 200 && !!(res?.data?.data?.context)) {
          this.updateSuccessState(chat, res?.data?.data, hasMarkdown);
        } else {
          this.updateErrorState(chat, res?.data?.errors[0]);
        }
        if (res?.data && res?.data?.chat_object?.status === 200 && !!res?.data?.chat_object?.data) {
          const chatList = res?.data?.chat_object?.data ? res?.data?.chat_object?.data : null;
          this.updateChatInformation(chatList);
        }
      }, error: err => {
        this.updateErrorState(chat);
      }
    }).add(() => this.setQueryProgressState('DONE'));
  }

  public getPromptReportForAFile(formData: FormData, chat: SavedChatHistoryModel, hasMarkdown: boolean = false, hasIs: boolean = false, hasLoadingState: boolean = false) {
    this.setQueryProgressState('PROGRESS');
    hasLoadingState && this.setLoadingState();
    this.chatService.fetchPromptReportForAFile(formData).pipe(
      takeUntil(this.cancelRequest$)
    ).subscribe({
      next: res => {
        if (res?.data?.status === 200 && (!!(res?.data?.data?.context) || !!(res?.data?.data?.markdown?.string))) {
          this.updateSuccessState(chat, res?.data?.data, hasMarkdown, hasIs);
        } else {
          this.updateErrorState(chat, res?.data?.errors[0]);
        }
        if (res?.data && res?.data?.chat_object?.status === 200 && !!res?.data?.chat_object?.data) {
          const chatList = res?.data?.chat_object?.data ? res?.data?.chat_object?.data : null;
          this.updateChatInformation(chatList);
        }
      }, error: err => {
        this.updateErrorState(chat);
      }
    }).add(() => this.setQueryProgressState('DONE'));
  }

  public getGLForTbAccounts() {
    const formData: FormData = new FormData();
    formData.append('prompt_type', 'gl_data');
    formData.append('data', JSON.stringify({
      engagement_id: this.selectedEng()?.engId,
      clientName: this.selectedEng()?.clientName
    }));
    const chatObj = this.getChatSavingObj(this.selectedPrompt()?.prompt, this.selectedPrompt()?.historyName);
    formData.append('chat_object', JSON.stringify(chatObj));
    this.filterMsgInHistory();
    this.setLoadingState();
    this.setQueryProgressState('PROGRESS');
    this.chatService.fetchPromptReport(formData).pipe(
      takeUntil(this.cancelRequest$)
    ).subscribe({
      next: res => {
        if (res?.data?.status === 200 && !!(res?.data?.data?.context)) {
          const inputInfo: LucaInputsModel<GLTbYearModel[]> = new LucaInputsModel<GLTbYearModel[]>();
          inputInfo.inputType = 'GENERAL_LEDGER';
          const glSelection: GLSelectionModel = new GLSelectionModel();
          glSelection.originalGl = res?.data?.data?.context;
          glSelection.isDisabled = false;
          inputInfo.isDisabled = false;
          inputInfo.glSelection = glSelection;
          const chat = this.aiUtil?.setWorkFlowInfo('GENERAL_LEDGER');
          chat.specialPromptData = structuredClone(inputInfo);
          chat.lucaOutputFilePath = res?.data?.data?.file_path;
          const currentChat = structuredClone(this.chatHistory());
          currentChat?.messages.pop();
          currentChat?.messages?.push(chat);
          this.chatHistory.set(structuredClone(currentChat));
          this.filePath.set(res?.data?.data?.file_path);
        } else {
          this.updateErrorState(structuredClone(this.chatHistory()), res?.data?.errors[0]);
        }
        if (res?.data && res?.data?.chat_object?.status === 200 && !!res?.data?.chat_object?.data) {
          this.updateChatInformation(res?.data?.chat_object?.data ? res?.data?.chat_object?.data : null);
        }
      },
      error: err => {
        this.updateErrorState(structuredClone(this.chatHistory()));
      }
    }).add(() => {
      this.setQueryProgressState('DONE');
    });
  }

  private updateSuccessState(chat: SavedChatHistoryModel, data: AIResModel, hasMarkdown: boolean = false, hasIs: boolean = false) {
    chat?.messages?.pop();
    const messages = structuredClone(chat);
    const updateChat: ChatLucaMsgModel = new ChatLucaMsgModel();
    updateChat.showSpinner = false;
    updateChat.messageFrom = 'luca';
    updateChat.lucaMarkdownResponse = data?.markdown['string'] ?? '';
    updateChat.lucaResponseHtml = data?.markdown['html'];
    updateChat.lucaResponseType = hasMarkdown ? 'MARKDOWN' : 'HTML';
    updateChat.messageType = 'ASSISTANT';
    updateChat.lucaOutputFilePath = data?.file_path ?? '';
    let setChat: ChatLucaMsgModel;
    const hasConfirmation = messages?.messages?.some(m => m?.specialPromptData?.hasConfirmation === true);

    if (hasIs && (data?.hasOwnProperty('hasInvalidFile') && data?.hasInvalidFile)) {
      setChat = this.aiUtil?.getPromptInfo(this.chatHistory()?.promptId, true, false, data?.markdown['string'] ?? '');
      this.chatService.attachedFiles.next(null);
      this.setQueryProgressState('PROGRESS');
    } else if (hasIs && !hasConfirmation && (data?.hasOwnProperty('hasAllMonths') && !data?.hasAllMonths)) {
      setChat = this.aiUtil?.getPromptInfo(this.chatHistory()?.promptId, false, true, data?.markdown['string'] ?? '');
      this.setQueryProgressState('PROGRESS');
    } else if (hasIs && data?.hasOwnProperty('hasMissingInputs') && data?.hasMissingInputs) {
      if (this.chatHistory()?.promptId === 9) {
        setChat = this.aiUtil.getAiChatMsg('WORKFLOW', 'Loan Amortization Schedule');
        setChat.specialPromptMessage = 'LAS';
        setChat.specialPromptData = {
          'hasMissingInputs': true,
          'inputType': 'LAS',
          'loanInfo': data?.loanInfo,
          'message': data?.markdown['string'] ?? ''
        };
      }
      if (this.chatHistory()?.promptId === 7) {
        setChat = this.aiUtil.getAiChatMsg('WORKFLOW', 'Bank Reconciliation');
        setChat.specialPromptMessage = 'RECONCILIATION';
        setChat.specialPromptData = {
          'hasMissingInputs': true,
          'inputType': 'RECONCILIATION',
          'reconInfo': data?.loanInfo,
          'message': data?.markdown['string'] ?? ''
        };
      }
      this.setQueryProgressState('PROGRESS');
    } else if (hasIs && data?.hasOwnProperty('hasInValidInfo') && data?.hasInValidInfo) {
      if (this.chatHistory()?.promptId === 9) {
        setChat = this.aiUtil.getAiChatMsg('WORKFLOW', 'Loan Amortization Schedule');
        setChat.specialPromptMessage = 'LAS';
        setChat.specialPromptData = {
          'hasInValidInfo': true,
          'hasMissingInputs': false,
          'inputType': 'LAS',
          'loanInfo': data?.loanInfo,
          'message': data?.markdown['string'] ?? ''
        };
      }
      if (this.chatHistory()?.promptId === 7) {
        setChat = this.aiUtil.getAiChatMsg('WORKFLOW', 'Bank Reconciliation');
        setChat.specialPromptMessage = 'RECONCILIATION';
        setChat.specialPromptData = {
          'hasInValidInfo': true,
          'hasMissingInputs': false,
          'inputType': 'RECONCILIATION',
          'reconInfo': {
            'hasNoValidRecon': true,
            'tbAccounts': this.reconInfo()?.tbAccounts,
            'plaidAccounts': this.reconInfo()?.plaidInstitutions,
            'selectedTBAccountUUID': this.reconInfo()?.selectedTBAccountUUID,
            'selectedPlaidAccount': this.reconInfo()?.selectedPlaidAccount
          },
          'message': data?.markdown['string'] ?? ''
        };
      }
      this.setQueryProgressState('PROGRESS');
      this.chatService.attachedFiles.next(null);
    }
    chat.filepathPromptGeneratedFile = data?.file_path;
    chat.messages = (!data?.hasAllMonths && setChat?.messageType === 'WORKFLOW') ? [...chat?.messages, structuredClone(setChat)] : [...chat?.messages, updateChat];
    this.chatHistory.set(structuredClone(chat));
    this.filePath.set(data?.file_path);
    this.context.set(data?.context);
    this.cdr.detectChanges();
    this.endOfPage.nativeElement.scrollIntoView({ behavior: 'smooth' });
  }

  private updateErrorState(chat: SavedChatHistoryModel, errorMessages: string = '') {
    chat?.messages.pop();
    const updateChat: ChatLucaMsgModel = new ChatLucaMsgModel();
    updateChat.showSpinner = false;
    updateChat.messageFrom = 'luca';
    updateChat.lucaMarkdownResponse = errorMessages ? errorMessages : this.aiResponses?.resError;
    updateChat.lucaResponseHtml = '';
    updateChat.lucaResponseType = 'MARKDOWN';
    updateChat.messageType = 'ASSISTANT';
    chat.messages = [...chat?.messages, updateChat];
    this.context.set('');
    this.chatHistory.set(structuredClone(chat));
  }

  private updateChatInformation(chat: ChatsModel) {
    if (chat?.id && chat?.filePath) {
      const originalChats: ChatsModel[] = structuredClone(this.chatsList());
      if (!(originalChats?.some(existingChat => existingChat.id === chat.id))) {
        this.chatsList.set(JSON.parse(JSON.stringify(originalChats?.length > 0 ? [...originalChats, chat] : [chat])));
      }
      this.selectedChat.set({...chat});
      this.chatHistory().chat_id = chat?.id;
      this.chatHistory().context = structuredClone(this.context());
    }
  }

  public newChat(isNewChat: boolean = false) {
    if (!isNewChat) {
      return;
    }
    this.resetControls();
    this.cdr.detectChanges();
    const chatInfo = this.AiMsg?.setNewChat(this.selectedEng()?.engId, this.selectedEng()?.isRestricted);
    this.chatHistory.set(structuredClone(chatInfo));
    this.setActionState(0, false, 'RESET');
  }

  private resetControls() {
    this.userInput.reset();
    this.uploadedFiles = null;
    this.userInput.enable({emitEvent: true});
    this.selectedPrompt.set(null);
    this.attachedFiles.set([]);
    this.filePath.set(null);
    this.context.set(null);
    this.isFetchingChat.set(false);
    this.draftPromptInputs.set(null);
    this.hasdraftInputs.set(false);
    this.chatHistory.set(null);
    this.selectedChat.set(null);
    this.cancelRequest$.next();
    this.chatService.attachedFiles.next(null);
  }

  public navigateToHistory(chat: ChatsModel) {
    this.index = 1000;
    this.filePath.set(null);
    this.context.set(null);
    this.chatHistory.set(null);
    this.attachedFiles.set([]);
    this.draftPromptInputs.set(null);
    this.hasdraftInputs.set(false);
    this.isFetchingChat.set(true);
    this.selectedChat.set(chat);
    this.setQueryProgressState('PROGRESS');
    this.getChatFromS3(chat?.filePath, true);
    this.setActionState(0, false, 'RESET');
  }

  private getChatFromS3(filePath: string = '', updateState: boolean = false) {
    if (!filePath) {
      return;
    }
    this.apiService.getJsonFirmTemplate({
      status: 0,
      signatureKey: filePath
    }).subscribe({
      next: res => {
        if (res) {
          const Obj: SavedChatHistoryModel = JSON.parse(res) ?? null;
          this.isFetchingChat.set(false);
          this.chatHistory.set(Obj);
          this.selectedPrompt.set(this.suggestedPrompts()?.find(p => p?.id === Obj?.promptId));
          this.filePath.set(Obj?.filepathPromptGeneratedFile);
          this.scrollToContent();
        }
      }
    }).add(() => {
      if (updateState) {
        this.setQueryProgressState('DONE');
      }
    });
  }

  private getFileName = (promptId: number): string => {
    return PROMPTS.find(p => p?.id === promptId)?.prompt ?? 'Ai-Generated-File';
  };

  public downloadResponse(index: number, typeOfExport: 'PDF' | 'XLSX' | 'DOCX', chat: ChatLucaMsgModel = null, isSaveToEngagement: boolean = false) {
    const obj: AiDownloadModel = new AiDownloadModel();
    obj.engagementId = this.selectedEng()?.engId ? this.selectedEng()?.engId : this.chatHistory()?.engagementId;
    obj.isSaveToEngagement = isSaveToEngagement;
    obj.exportType = typeOfExport;
    obj.fileName = this.selectedChat()?.title ? this.selectedChat()?.title : this.getFileName(this.chatHistory()?.promptId);
    if (typeOfExport === 'XLSX' && !!chat?.lucaOutputFilePath) {
      obj.hasSystemFile = true;
      obj.filePath = chat?.lucaOutputFilePath ?? '';
      this.download$.next(structuredClone(obj));
      return;
    }
    const htmlCollection = document.getElementsByClassName(`response-${index}`);
    if (chat?.messageType === 'ASSISTANT' && htmlCollection && htmlCollection?.length > 0) {
      obj.inputHtml = htmlCollection[0]?.innerHTML;
      obj.hasSystemFile = false;
      this.download$.next(structuredClone(obj));
    } else if (chat?.messageType === 'SYSTEM_AI') {
      obj.inputHtml = `<p>${chat?.lucaMarkdownResponse}</p>`;
      obj.hasSystemFile = false;
      this.download$.next(structuredClone(obj));
    }
  }

  public downloadCustomFile(typeOfExport: 'PDF' | 'XLSX' | 'DOCX',
                            isSaveToEngagement: boolean = false, chat: ChatLucaMsgModel = null) {
    const obj: AiDownloadModel = new AiDownloadModel();
    obj.engagementId = this.selectedEng()?.engId;
    obj.isSaveToEngagement = isSaveToEngagement;
    obj.exportType = typeOfExport;
    obj.fileName = this.getFileName(this.selectedChat()?.promptId);
    if (chat?.specialPromptMessage === 'GENERAL_LEDGER' && chat?.specialPromptData?.glSelection?.selectedRows?.length > 0) {
      const inputInfo = {'generalLedger': chat?.specialPromptData?.glSelection?.selectedRows?.map(g => g?.transactions)?.flat(Infinity)};
      this.convertToExcel(inputInfo, obj, 'General Ledger');
      return;
    }
    if (chat?.specialPromptMessage === 'CAAS_SCHEDULE' && chat?.specialPromptData?.isDisabled) {
      const inputInfo = {
        'generalLedger': chat?.specialPromptData?.generalLedger,
        'amortizationSchedule': chat?.specialPromptData?.scheduleRows,
        'tbAccounts': chat?.specialPromptData?.tbAccounts,
        'adjustmentEntries': chat?.specialPromptData?.adjustmentEntries,
        'amortizationInputs': chat?.specialPromptData?.prefAccounts,
        'calculationTable': this.aiUtil.transformDetailsForExport(JSON.parse(JSON.stringify(chat?.specialPromptData?.scheduleRows)))
      };
      this.convertToExcel(inputInfo, obj, 'Capital Amortization');
      return;
    }
    obj.hasSystemFile = true;
    obj.filePath = chat?.lucaOutputFilePath;
    this.download$.next(structuredClone(obj));
  }

  public setActionState(index: number, isSaveToEngagement: boolean = false, type: 'SET' | 'UPDATE' | 'RESET' | 'FAILED' = 'SET') {
    if (type === 'RESET') {
      this.actionMessage.set(null);
      this.index = 1000;
      return;
    }
    this.index = index;
    const action: ResponseActionModel = new ResponseActionModel();
    action.actionProgress = type === 'SET' ? 'PROGRESS' : (type === 'FAILED' ? 'FAILED' : 'COMPLETED');
    action.actionType = isSaveToEngagement ? 'SAVE' : 'DOWNLOAD';
    action.index = index;
    this.actionMessage.set(structuredClone(action));
    if (type === 'UPDATE' || type === 'FAILED') {
      setTimeout(() => {
        action.actionType = 'NONE';
        this.actionMessage.set(structuredClone(action));
      }, 3000);
    }
  }

  reset(): void {
    this.recognizing = false;
  }

  closeDialog() {
    const saveState: LucaClosedModel = new LucaClosedModel();
    saveState.closedType = 'CLOSE';
    this.triggerAction.emit(saveState);
  }

  public minimizeWindow(actionType: 'MINIMIZE' | 'CONVERTED' = 'MINIMIZE') {
    const saveState: LucaClosedModel = new LucaClosedModel();
    const history = JSON.parse(JSON.stringify(this.chatHistory()));
    if (actionType === 'MINIMIZE') {
      if (this.hasdraftInputs()) {
        history?.messages?.forEach(m => {
          if (m?.messageType === 'WORKFLOW' && m?.specialPromptMessage === 'CAAS_PREFERENCES') {
            m.specialPromptData = structuredClone({
              'prefAccounts': this.draftPromptInputs()?.prefAccounts,
              'prefCategories': this.draftPromptInputs()?.prefCategories,
              'isDisabled': false,
              'inputType': 'CAAS_PREFERENCES'
            });
          }
          if (m?.messageType === 'WORKFLOW' && m?.specialPromptMessage === 'GENERAL_LEDGER') {
            m.specialPromptData = structuredClone(this.draftPromptInputs());
          }
        });
      }
      saveState.chatHistory = JSON.parse(JSON.stringify(this.chatHistory()));
      saveState.selectedPrompt = this.selectedPrompt();
      saveState.context = this.context();
      saveState.filePath = this.filePath();
      saveState.isFromHistory = this.isFromHistory();
      saveState.engagementInfo = this.selectedEng();
      saveState.activeChatInfo = this.selectedChat();
      saveState.chatsList = this.chatsList();
      saveState.attachedFiles = this.attachedFiles();
      saveState.contextRes = JSON.parse(JSON.stringify(this.contextRes()));
      saveState.hasOnGoingApiCallUUID = this.chatService.currentUUID;
      this.chatService.isMinimized = true;
      this.chatService.uploadedFiles.next(this.uploadedFiles);
    }
    saveState.closedType = actionType;
    saveState.disableLauncher = actionType === 'CONVERTED';
    this.triggerAction.emit(saveState);
  }

  public getClosingState() {
    const stateInfo: AiStateModel = new AiStateModel();
    stateInfo.engList = JSON.parse(JSON.stringify(this.originalEngList()));
    stateInfo.chatsList = JSON.parse(JSON.stringify(this.chatsList()));
    stateInfo.hasActiveChat = !!this.selectedChat()?.id ;
    stateInfo.hasSelectedEng = !!this.selectedEng()?.engId;
    stateInfo.selectedEng = stateInfo.hasSelectedEng ? this.selectedEng() : null;
    stateInfo.activeChat = stateInfo.hasActiveChat ? this.selectedChat() : null;
    stateInfo.chatHistory = stateInfo.hasActiveChat ? null : this.chatHistory();
    stateInfo.selectedPrompt = stateInfo.hasActiveChat ? null : this.selectedPrompt();
    stateInfo.hasNoEngagements = this.originalEngList()?.length === 0;
    stateInfo.pageInfo = stateInfo.hasSelectedEng ? null : this.dialogInfo()?.pageInfo;
    return stateInfo;
  }

  public convertWindowToDialog() {
    const saveState: LucaClosedModel = new LucaClosedModel();
    saveState.chatsList = this.chatsList();
    saveState.disableLauncher = false;
    saveState.closedType = 'MINIMIZE';
    if (this.selectedEng()?.engId > 0 && this.selectedChat()?.id > 0) {
      saveState.engagementInfo = this.selectedEng();
      saveState.activeChatInfo = this.selectedChat();
    } else if (this.selectedEng()?.engId > 0 && !(this.selectedChat()?.id > 0)) {
      saveState.engagementInfo = this.selectedEng();
      saveState.chatHistory = this.chatHistory();
    } else if (this.hasNoSelection()) {
      saveState.chatHistory = this.chatHistory();
    }
    this.triggerAction.emit(saveState);
  }

  public resizeWindow(state: 'DEFAULT' | 'FULL') {
    (state === 'DEFAULT') && this.setDefaultState('FULL');
    (state === 'FULL') && this.setDefaultState('DEFAULT');
  }

  public setDefaultState(type: 'DEFAULT' | 'FULL' | 'INIT') {
    const resize: DialogResizeModel = new DialogResizeModel();
    if (type === 'DEFAULT' || type === 'INIT') {
      resize.state = 'DEFAULT';
      resize.iconClass = "fa-regular fa-arrow-up-right-and-arrow-down-left-from-center text-white";
      resize.hasExpanded = false;
      resize.title = 'Expand';
    }
    if (type === 'FULL') {
      resize.state = 'FULL';
      resize.iconClass = "fa-light fa-arrow-down-left-and-arrow-up-right-to-center text-white";
      resize.hasExpanded = true;
      resize.title = 'Default';
    }
    this.expandState.set(structuredClone(resize));
  }

  private updatePageInformation(pageInfo: pageInfo = null) {
    this.chatService.resetValues();
    if ((!pageInfo || pageInfo?.clientId === 0) && (pageInfo?.pageType !== 'NONE')) {
      return;
    }
    const chatInfo: SavedChatHistoryModel = new SavedChatHistoryModel();
    chatInfo.messages = [];
    const systemMsg: ChatLucaMsgModel = new ChatLucaMsgModel();
    systemMsg.showSpinner = false;
    systemMsg.messageType = 'SYSTEM_AI';
    systemMsg.lucaResponseType = 'MARKDOWN';
    if (pageInfo?.pageType === 'CLIENT') {
      const engList = structuredClone(this.engagementsList());
      const filterList = engList?.filter(e => e?.clientId === pageInfo?.clientId);
      if (filterList?.length === 1) {
        systemMsg.lucaMarkdownResponse = filterList[0]?.isRestricted ? this.aiResponses.restrictEngagement : this.aiResponses.engSelected;
        chatInfo.messages = [systemMsg];
        chatInfo.engagementId = filterList[0]?.engId;
        this.searchText.setValue(filterList[0]?.engName, {emitEvent: true});
        this.searchText.updateValueAndValidity({onlySelf: false, emitEvent: true});
        this.searchEngText.set(filterList[0]?.engName);
        this.selectedEng.set(filterList[0]);
        this.setPromptByEngType(this.selectedEng()?.engTypeId);
        this.getHistory(this.selectedEng()?.engId);
        this.getEngagementSourceInfo(this.selectedEng()?.engId);
      } else if (filterList?.length > 1) {
        this.searchText.setValue(pageInfo?.clientName, {emitEvent: true});
        this.searchText.updateValueAndValidity({onlySelf: false, emitEvent: true});
        this.searchEngText.set(pageInfo?.clientName);
        systemMsg.lucaMarkdownResponse = this.aiResponses.selectEngagement;
        this.engagementsList.set(filterList);
        chatInfo.messages = [systemMsg];
      } else if (filterList?.length === 0 || !filterList) {
        this.hasNoEngagements.set(true);
      }
    }
    if (pageInfo?.pageType === 'ENGAGEMENT' && pageInfo?.engId !== 0) {
      this.searchText.setValue(pageInfo?.engName);
      this.searchText.updateValueAndValidity({onlySelf: false, emitEvent: true});
      this.searchEngText.set(pageInfo?.engName);
      this.selectedEng.set(this.getEngFromPage(pageInfo?.engId));
      this.setPromptByEngType(this.selectedEng()?.engTypeId);
      systemMsg.lucaMarkdownResponse = this.selectedEng()?.isRestricted ? this.aiResponses.restrictEngagement : this.aiResponses.engSelected;
      chatInfo.messages = [systemMsg];
      chatInfo.engagementId = this.selectedEng()?.engId;
      this.getHistory(this.selectedEng()?.engId);
      this.getEngagementSourceInfo(this.selectedEng()?.engId);
    }
    if (pageInfo?.pageType === 'NONE') {
      systemMsg.lucaMarkdownResponse = this.aiResponses.selectEngagement;
      chatInfo.messages = [systemMsg];
    }
    this.chatHistory.set(structuredClone(chatInfo));
    this.setQueryProgressState('RESTRICTED');
  }

  private getEngFromPage(engagementId: number) {
    const engList = structuredClone(this.engagementsList());
    return engList.find(e => e?.engId === engagementId);
  }

  private sessionManagementDialog(updatedEngId: number = 0) {
    this.dialog.open(this.sessionManagement, {
      disableClose: true
    }).afterClosed().subscribe({
      next: res => {
        if (res) {
          this.setNewOrRefreshSession(updatedEngId);
        } else {
          this.hasRefresh.set(true);
        }
      }
    });
  }

  public refreshDialog() {
    this.dialog.open(this.refreshSession, {
      disableClose: true
    }).afterClosed().subscribe({
      next: res => {
        if (res) {
          this.hasRefresh.set(false);
          this.setNewOrRefreshSession(this.dialogInfo()?.pageInfo?.engId);
        } else {
          this.hasRefresh.set(true);
        }
      }
    });
  }

  public onChangeFile(event) {
    if (event.target.files && event.target.files.length > 0) {
      const input = event?.target as HTMLInputElement;
      let selectedFiles = [];
      if (input?.files) {
        selectedFiles = Array.from(input?.files);
      }
      let totalFileSizeMB = 0;
      selectedFiles?.forEach((f: File) => {
        totalFileSizeMB += f.size / (1024 * 1024);
      });
      this.uploadedFiles = this.uploadedFiles?.length > 0 ? this.uploadedFiles.concat(selectedFiles) : selectedFiles;
      const repoFiles = this.attachedFiles()?.filter(f => f?.fileOrigin === 'REPO')
      this.attachedFiles.set(
        this.aiUtil.updateTransformFileInputs((repoFiles ?? []),
          this.uploadedFiles, this.allowedExt())
      );
      this.fileInput.nativeElement.value = '';
      this.inputImgPath.set(structuredClone(VOICE_SEND_INPUT));
      this.inputImgPath().isDisabled = false;
    }
  }

  public setEngagement(event: ChangeEngModel) {
    this.listMenu?.closeMenu();
    if (event?.type === 'NEW' || event?.type === 'NORMAL') {
      this.chatService.attachedFiles.next(null);
      this.setNewOrRefreshSession(event?.engId);
    } else {
      this.searchEngText.set(event?.searchText);
      this.searchText.setValue(event?.searchText);
      this.setQueryProgressState('RESTRICTED');
    }
    if (event?.engId > 0) {
      this.selectedChat.set(null);
    }
  }

  getEngagementSourceInfo(engagementId: number = 0) {
    if (engagementId === 0) {
      return;
    }
    this.engService.fetchIntegrationByEngagement(engagementId).pipe(
      distinctUntilChanged()
    ).subscribe({
      next: response => {
        if (response && response?.id) {
          const sourceInfo = this.aiUtil.getEngIntegrationInfo(response);
          const updateInfo = this.selectedEng();
          updateInfo.sourceType = sourceInfo?.sourceName;
          updateInfo.isSourceEligible = ['QUICKBOOK', 'XERO'].includes(updateInfo.sourceType);
          updateInfo.isActiveConnection = sourceInfo?.isConnected;
          updateInfo.logoPath = sourceInfo?.logoPath;
          updateInfo.sourceClientName = sourceInfo?.orgName;
          updateInfo.integrationId = sourceInfo?.integrationId;
          this.selectedEng.set(structuredClone(updateInfo));
        } else {
          const updateInfo = this.selectedEng();
          updateInfo.sourceType = 'NONE';
          updateInfo.isSourceEligible = false;
          updateInfo.isActiveConnection = false;
          updateInfo.logoPath = '';
          this.selectedEng.set(structuredClone(updateInfo));
        }
      }
      , error: err => {
        const updateInfo = this.selectedEng();
        updateInfo.sourceType = 'NONE';
        updateInfo.isSourceEligible = false;
        updateInfo.isActiveConnection = false;
        updateInfo.logoPath = '';
        this.selectedEng.set(structuredClone(updateInfo));
      }
    }).add(() => {
      this.getContextDetails();
    });
  }

  private updateMinimizeState(dialogState: LucaClosedModel) {
    this.selectedEng.set(dialogState?.engagementInfo);
    this.searchText.setValue(this.selectedEng()?.engName, {emitEvent: false, onlySelf: true});
    this.setPromptByEngType(this.selectedEng()?.engTypeId);
    this.selectedPrompt.set(dialogState?.selectedPrompt);
    this.context.set(dialogState?.context);
    this.filePath.set(dialogState?.filePath);
    this.engagementsList.set(this.dialogInfo()?.engList);
    this.originalEngList.set(this.dialogInfo()?.engList);
    this.chatHistory.set(dialogState?.chatHistory);
    this.chatsList.set(dialogState?.chatsList);
    this.selectedChat.set(dialogState?.activeChatInfo);
    this.searchText.enable();
    this.attachedFiles.set(dialogState?.attachedFiles);
    this.uploadedFiles = this.chatService.uploadedFiles.getValue();
    if (!this.hasNoSelection()) {
      this.userInput.enable();
    }
    this.checkForUpdatedEngagement(dialogState?.engagementInfo?.engId, this.dialogInfo()?.pageInfo);
    if (this.selectedChat()?.id > 0 && !(this.chatHistory()?.messages?.length > 0)) {
      this.navigateToHistory(structuredClone(this.selectedChat()));
    }
    this.updateResponseStateForAPrompt(dialogState?.hasOnGoingApiCallUUID);
    if(dialogState?.contextRes?.trialBalance?.length > 0) {
      this.contextRes.set(dialogState?.contextRes);
      this.isContextLoading.set(!(dialogState?.contextRes?.trialBalance?.length > 0))
    } else {
      this.getContextDetails();
    }
  }

  private checkForUpdatedEngagement(originalEngId: number = 0, pageInfo: pageInfo = null) {
    if (pageInfo?.pageType === 'ENGAGEMENT' && pageInfo?.engId !== 0) {
      if (pageInfo?.engId !== originalEngId) {
        setTimeout(() => {
          this.sessionManagementDialog(pageInfo?.engId);
        }, 500);
      }
    }
  }

  private setNewOrRefreshSession(updatedEngId: number = 0) {
    const eng = this.originalEngList()?.find(e => e?.engId === updatedEngId);
    this.searchText.setValue(eng?.engName);
    eng && this.selectedEng.set(structuredClone(eng));
    this.chatHistory.set(null);
    this.cdr.detectChanges();
    this.selectedPrompt.set(null);
    this.attachedFiles.set(null);
    this.uploadedFiles = null;
    this.chatsList.set(null);
    this.chatHistory.set(structuredClone(this.AiMsg?.setNewChat(this.selectedEng()?.engId, this.selectedEng()?.isRestricted)));
    this.setPromptByEngType(this.selectedEng()?.engTypeId);
    this.getHistory(this.selectedEng()?.engId);
    this.getEngagementSourceInfo(this.selectedEng()?.engId);
    this.setQueryProgressState('RESTRICTED');
  }

  stopRecording() {
    this.voice2Txt.stop();
    this.destroyVoice$.next('');
    this.destroyVoice$.complete();
    this.destroyVoice$ = new Subject<any>();
    this.isUserSpeaking = false;
    this.subscription.unsubscribe();
  }

  initVoiceInput() {
    this.voice2Txt.init().subscribe(() => {
    })
    this.voice2Txt.voice2Txt$.pipe(
      takeUntil(this.destroyVoice$)
    ).subscribe((input) => {
      if (input) {
        this.userInput.setValue(input);
      }
    });
    this.initializeSpeechRecognition();
  }

  startRecording() {
    this.destroyVoice$ = new Subject<any>();
    this.isUserSpeaking = true;
    this.voice2Txt.start();
  }

  private getLoadingContent() {
    return this.aiResponses?.loadingContent;
  }

  public updateVr(data: Partial<LucaInputsModel>, chat: (ChatUserMsgModel | ChatLucaMsgModel)) {
    if (chat?.specialPromptMessage === 'VARIANCE_ANALYSIS_INPUTS') {
      chat.specialPromptData = {
        'matPercent': data?.matPercent,
        'matVal': data?.matVal,
        'isDisabled': true,
        'inputType': 'VARIANCE_ANALYSIS_INPUTS'
      };
    }
    const inputObj: CommonInputModel = new CommonInputModel();
    inputObj.engagement_id = this.selectedEng()?.engId;
    inputObj.clientName = this.selectedEng()?.clientName;
    inputObj.type = 'variance_analysis_with_api';
    inputObj.change_dollar_input = data?.matVal;
    inputObj.change_percent_input = data?.matPercent;
    this.setHistoryByChatMsg(structuredClone(chat), 'REMOVE');
    inputObj.chat_object = this.getChatSavingObj(this.selectedPrompt()?.prompt, this.selectedPrompt()?.historyName, 'ALL');
    this.getPromptReport(inputObj, structuredClone(this.chatHistory()), false);
  }

  public updateAttachedFiles(data: Partial<LucaInputsModel>, chat: (ChatUserMsgModel | ChatLucaMsgModel)) {
    this.setAttachedFiles(data?.selectedFiles);
    const attachedFiles = this.chatService.attachedFiles.getValue();
    const filesList = Array.from(attachedFiles?.values());
    const updatedList = Array.from(attachedFiles?.values());
    data.attachedFiles = this.aiUtil.convertFilesToSaveInfo(filesList);
    const aiChat = this.aiUtil?.getAiChatMsg('SYSTEM_PROMPT_RES_LOADING', this.getLoadingContent());
    const formData: FormData = new FormData();
    updatedList?.forEach(f => {
      formData.append('files', f);
    });
    const input: CommonInputModel = new CommonInputModel();
    input.engagement_id = this.selectedEng()?.engId;
    input.clientName = this.selectedEng()?.clientName;
    input.confirmation = !!data?.hasConfirmation;
    input.engagement_name = this.selectedEng()?.engName;
    if (data?.inputType === 'IS') {
      chat.specialPromptData = {
        'investmentConfirmation': data?.hasConfirmation,
        'inputType': data?.inputType,
        'isDisabled': true,
        'attachedFiles': data.attachedFiles,
        'message': data?.message
      };
      this.setHistoryByChatMsg(structuredClone(chat), 'REMOVE');
      formData.append('data', JSON.stringify(input));
      formData.append('prompt_type', 'investment_schedule_analysis');
      this.setHistoryByChatMsg(aiChat, 'PUSH');
      const chatObj = this.getChatSavingObj(this.selectedPrompt()?.prompt, this.selectedPrompt()?.historyName);
      chatObj.previous_messages_to_append = this.getPreviousMessages(structuredClone(data));
      formData.append('chat_object', JSON.stringify(chatObj));
      this.getPromptReportForAFile(formData, structuredClone(this.chatHistory()), false, true);
    }
    if (data?.inputType === 'HST') {
      chat.specialPromptData = {
        'investmentConfirmation': data?.hasConfirmation,
        'inputType': data?.inputType,
        'isDisabled': true,
        'attachedFiles': data.attachedFiles,
        'message': data?.message
      };
      this.setHistoryByChatMsg(structuredClone(chat), 'REMOVE');
      formData.append('prompt_type', 'hst_gst_plausibility');
      formData.append('data', JSON.stringify(input));
      this.setHistoryByChatMsg(aiChat, 'PUSH');
      const chatObj = this.getChatSavingObj(this.selectedPrompt()?.prompt, this.selectedPrompt()?.historyName);
      formData.append('chat_object', JSON.stringify(chatObj));
      this.getPromptReportForAFile(formData, structuredClone(this.chatHistory()), false, true);
    }
    if (data?.inputType === 'LAS') {
      if (!data?.hasMissingInputs) {
        chat.specialPromptData = {
          'investmentConfirmation': data?.hasConfirmation,
          'inputType': data?.inputType,
          'isDisabled': true,
          'attachedFiles': data.attachedFiles,
          'message': data?.message
        };
        this.setHistoryByChatMsg(structuredClone(chat), 'REMOVE');
      }
      input.hasMissingInputs = data?.hasMissingInputs;
      input.hasInValidInfo = data?.hasInValidFile;
      input.loanInfo = data?.loanInfo;
      input.acctYear = this.selectedEng()?.year;
      formData.append('prompt_type', 'loan_amortization');
      formData.append('data', JSON.stringify(input));
      this.setHistoryByChatMsg(aiChat, data?.hasMissingInputs ? 'REMOVE' : 'PUSH');
      const chatObj = this.getChatSavingObj(this.selectedPrompt()?.prompt, this.selectedPrompt()?.historyName);
      chatObj.previous_messages_to_append = this.getPreviousMessages(structuredClone(data));
      formData.append('chat_object', JSON.stringify(chatObj));
      this.getPromptReportForAFile(formData, structuredClone(this.chatHistory()), false, true);
    }
    if(data?.inputType === 'RECONCILIATION') {
      chat.specialPromptData = {
        'investmentConfirmation': data?.hasConfirmation,
        'inputType': data?.inputType,
        'isDisabled': true,
        'attachedFiles': data.attachedFiles,
        'message': data?.message,
        'reconInfo': {
          'selectedTBAccountUUID': data?.reconInfo?.selectedTBAccountUUID,
          'selectedPlaidAccount': data?.reconInfo?.selectedPlaidAccount,
          'hasPlaid': data?.reconInfo?.hasPlaid,
          'tbAccounts': data?.reconInfo?.tbAccounts,
          'plaidAccounts': data?.reconInfo?.plaidInstitutions
        }
      };
      this.reconInfo.update(r => {
        r.selectedPlaidAccount = data?.reconInfo?.selectedPlaidAccount;
        r.selectedTBAccountUUID = data?.reconInfo?.selectedTBAccountUUID;
        return {...r};
      });
      this.setHistoryByChatMsg(aiChat, 'PUSH');
      input.acctYear = this.selectedEng()?.year;
      input.hasPlaid = data?.reconInfo?.hasPlaid;
      input.tbAccountUUID = data?.reconInfo?.selectedTBAccountUUID;
      input.plaidAccountId = data?.reconInfo?.selectedPlaidAccount
      formData.append('prompt_type', 'bank_reconciliation');
      formData.append('data', JSON.stringify(input));
      const chatObj = this.getChatSavingObj(this.selectedPrompt()?.prompt, this.selectedPrompt()?.historyName);
      chatObj.previous_messages_to_append = this.getPreviousMessages(structuredClone(data));
      formData.append('chat_object', JSON.stringify(chatObj));
      this.getRecon(formData,structuredClone(this.chatHistory()));
    }
    this.cdr.detectChanges();
  }

  public updateGlSelection(data: Partial<LucaInputsModel>, chat: (ChatUserMsgModel | ChatLucaMsgModel)) {
    this.hasdraftInputs.set(false);
    chat.specialPromptData = structuredClone(data);
    const msg: ChatLucaMsgModel = new ChatLucaMsgModel();
    msg.promptType = 'GENERAL_LEDGER';
    msg.messageType = 'WORKFLOW';
    msg.specialPromptMessage = 'GENERAL_LEDGER';
    msg.specialPromptData = structuredClone(data);
    msg.lucaOutputFilePath = this.filePath();
    this.saveUserSelectedInputs(msg, null, false);
    this.chatHistory.update(chat => {
      chat?.messages?.forEach(m => {
        if (m?.messageType === 'WORKFLOW' && m?.specialPromptMessage === 'GENERAL_LEDGER') {
          m.specialPromptData = JSON.parse(JSON.stringify(data));
        }
      });
      return chat;
    });
  }

  convertToExcel(inputInfo: any, info: AiDownloadModel, promptType: 'Capital Amortization' | 'General Ledger') {
    const inputs: SaveExcelModel = new SaveExcelModel();
    inputs.engagement_name = this.selectedEng()?.engName;
    inputs.clientName = this.selectedEng()?.clientName;
    inputs.finanicalyearenddate = this.selectedEng()?.yearEnd;
    inputs.prompt_type = promptType;
    inputs.json_data = inputInfo;
    this.chatService.convertToExcel(inputs).subscribe({
      next: res => {
        if (res?.status === 200 && res?.data?.data?.file_path) {
          info.hasSystemFile = true;
          info.filePath = res?.data?.data?.file_path;
          this.download$.next(info);
        } else{
          this.setActionState(this.index, true, 'FAILED');
        }
      }
    });
  }

  public draftInputs(data: Partial<LucaInputsModel>) {
    this.hasdraftInputs.set(true);
    this.draftPromptInputs.set(structuredClone(data));
  }

  public updateAmortizationInputs(data: Partial<LucaInputsModel>, chat: (ChatUserMsgModel | ChatLucaMsgModel)) {
    this.hasdraftInputs.set(false);
    if (chat?.promptType === 'CAAS_PREFERENCES') {
      chat.specialPromptData = {
        'prefAccounts': data?.prefAccounts,
        'prefCategories': data?.prefCategories,
        'isDisabled': true,
        'inputType': 'CAAS_PREFERENCES'
      };
    }
    const msg: ChatLucaMsgModel = new ChatLucaMsgModel();
    msg.promptType = 'CAAS_PREFERENCES';
    msg.messageType = 'WORKFLOW';
    msg.specialPromptMessage = 'CAAS_PREFERENCES';
    msg.specialPromptData = structuredClone({
      'prefAccounts': data?.prefAccounts,
      'prefCategories': data?.prefCategories,
      'isDisabled': true,
      'inputType': 'CAAS_PREFERENCES'
    });

    const loadingObj: ChatLucaMsgModel = this.aiUtil?.getAiChatMsg('SYSTEM_PROMPT_RES_LOADING', this.getLoadingContent());
    const history = structuredClone(this.chatHistory());
    history?.messages?.pop();
    history?.messages?.push(chat);
    history.messages.push(loadingObj);
    this.chatHistory.set(structuredClone(history));
    this.saveUserSelectedInputs(msg, data, true);
  }

  public updateAmortizationSchedule(data: Partial<LucaInputsModel>) {
    this.hasdraftInputs.set(false);
    const msg: ChatLucaMsgModel = new ChatLucaMsgModel();
    msg.promptType = 'CAAS_SCHEDULE';
    msg.messageType = 'WORKFLOW';
    msg.specialPromptMessage = 'CAAS_SCHEDULE';
    msg.specialPromptData = structuredClone({
      'scheduleRows': data?.scheduleRows,
      'adjustmentEntries': data?.adjustmentEntries,
      'tbAccounts': data?.tbAccounts,
      'generalLedger': data?.generalLedger,
      'prefAccounts': data?.prefAccounts,
      'amortizationDetails': data?.amortizationDetails,
      'isDisabled': false,
      'inputType': 'CAAS_SCHEDULE'
    });
    msg.lucaOutputFilePath = this.filePath();
    this.saveUserSelectedInputs(msg, data, false);
  }

  public updateCAASValues(data: Partial<LucaInputsModel>, chat: (ChatUserMsgModel | ChatLucaMsgModel)) {
    if (chat?.promptType === 'CAAS_SCHEDULE') {
      chat.specialPromptData = structuredClone({
        'scheduleRows': data?.scheduleRows,
        'adjustmentEntries': data?.adjustmentEntries,
        'tbAccounts': data?.tbAccounts,
        'generalLedger': data?.generalLedger,
        'prefAccounts': data?.prefAccounts,
        'amortizationDetails': data?.amortizationDetails,
        'isDisabled': true,
        'inputType': 'CAAS_SCHEDULE'
      });

      const history = structuredClone(this.chatHistory());
      history?.messages?.pop();
      history?.messages?.push(chat);
      this.chatHistory.set(structuredClone(history));
    }
    const msg: ChatLucaMsgModel = new ChatLucaMsgModel();
    msg.promptType = 'CAAS_SCHEDULE';
    msg.messageType = 'WORKFLOW';
    msg.specialPromptMessage = 'CAAS_SCHEDULE';
    msg.specialPromptData = structuredClone({
      'scheduleRows': data?.scheduleRows,
      'adjustmentEntries': data?.adjustmentEntries,
      'amortizationDetails': data?.amortizationDetails,
      'tbAccounts': data?.tbAccounts,
      'generalLedger': data?.generalLedger,
      'isDisabled': false,
      'inputType': 'CAAS_SCHEDULE'
    });
    msg.lucaOutputFilePath = this.filePath();
    this.saveUserSelectedInputs(msg, data, false);
  }

  private setHistoryByChatMsg(chatObj: ChatUserMsgModel | ChatLucaMsgModel = null, type: 'REMOVE' | 'PUSH') {
    const history: SavedChatHistoryModel = JSON.parse(JSON.stringify(this.chatHistory()));
    if (type === 'REMOVE' && history?.messages && history?.messages?.length > 0) {
      history?.messages?.pop();
    }
    (history?.messages?.length > 0) && history?.messages.push(chatObj);
    history.messages = (history?.messages?.length > 0) ? history?.messages : [chatObj];
    this.chatHistory.set(structuredClone(history));
  }

  private getVr() {
    if (this.selectedEng()?.engTypeId === 1) {
      return;
    }
    this.filterMsgInHistory();
    const inputObj: CommonInputModel = new CommonInputModel();
    inputObj.engagement_id = this.selectedEng()?.engId;
    inputObj.type = 'variance_analysis_with_api';
    inputObj.clientName = this.selectedEng()?.clientName;
    inputObj.chat_object = this.getChatSavingObj(this.selectedPrompt()?.prompt, this.selectedPrompt()?.historyName, 'ALL');
    this.setLoadingState();
    this.getPromptReport(inputObj, structuredClone(this.chatHistory()), true);
  }

  private getAr() {
    const inputObj: CommonInputModel = new CommonInputModel();
    inputObj.engagement_id = this.selectedEng()?.engId;
    inputObj.type = 'ar_analysis';
    inputObj.engagement_name = this.selectedEng()?.engName;
    inputObj.clientName = this.selectedEng()?.clientName;
    inputObj.chat_object = this.getChatSavingObj(this.selectedPrompt()?.prompt, this.selectedPrompt()?.historyName);
    this.filterMsgInHistory();
    this.setLoadingState();
    this.getPromptReport(inputObj, structuredClone(this.chatHistory()), true);
  }

  private getChatSavingObj(promptText: string, query: string = '', appendType: 'ALL' | 'ONE' = 'ONE') {
    const chatInputs = new ChatSavingInputModel();
    chatInputs.chat_title = promptText;
    chatInputs.engagement_id = this.selectedEng()?.engId;
    chatInputs.is_first_message = !this.selectedChat()?.id;
    chatInputs.prompt_id = this.aiUtil?.getPromptId(this.selectedPrompt()?.id);
    chatInputs.chat_id = this.selectedChat()?.id ?? null;
    chatInputs.query = query;
    chatInputs.free_context = this.chatHistory()?.freeContext;
    const messages = structuredClone(this.chatHistory()?.messages);
    if (appendType === 'ONE') {
      (!this.selectedChat()?.id && messages?.length > 1) && messages.pop();
      (!this.selectedChat()?.id && messages?.length > 1) && messages.pop();
    }
    chatInputs.previous_messages_to_append = this.selectedChat()?.id ? [] : structuredClone(messages);
    chatInputs.chat_id = this.selectedChat()?.id ? this.selectedChat()?.id : null;
    const repoFiles = this.attachedFiles()?.filter(f => f?.fileOrigin === 'REPO')?.map(f => f?.filePath);
    if(repoFiles?.length > 0) {
      chatInputs.s3_file_paths = repoFiles;
    }
    return structuredClone(chatInputs);
  }

  addFilesToChat(lucaFiles: LucaFileModel[]) {
    const userChat = this.aiUtil?.getUserChatMsg('USER', '', lucaFiles?.map((f: any) => f?.fileName));
    userChat.specialPromptMessage = 'DFA';
    const allowedExtensions = ['pdf', 'txt', 'docx', 'csv', 'xlsx', 'xls'];
    const validFiles = lucaFiles?.filter(file => {
      const fileExtension = file?.fileName?.split('.')?.pop()?.toLowerCase();
      return fileExtension && allowedExtensions.includes(fileExtension);
    });
    this.attachedFiles.set(
      this.aiUtil.updateTransformRepoFileInputs(JSON.parse(JSON.stringify(this.attachedFiles())), validFiles, this.allowedExt())
    );
    this.fileInput.nativeElement.value = '';
    this.inputImgPath.set(structuredClone(VOICE_SEND_INPUT));
    this.inputImgPath().isDisabled = false;
  }

  public getAmortizationInputs() {
    const formData: FormData = new FormData();
    formData.append('prompt_type', 'capital_assets');
    formData.append('data', JSON.stringify({
      engagement_id: this.selectedEng()?.engId,
      clientName: this.selectedEng()?.clientName
    }));
    const chatObj = this.getChatSavingObj(this.selectedPrompt()?.prompt, this.selectedPrompt()?.historyName);
    formData.append('chat_object', JSON.stringify(chatObj));
    this.setQueryProgressState('PROGRESS');
    this.chatService.fetchPromptReport(formData).pipe(
      takeUntil(this.cancelRequest$)
    ).subscribe({
      next: res => {
        if (res?.data?.status === 200 && !!(res?.data?.data?.context)) {
          const inputInfo: LucaInputsModel<any> = new LucaInputsModel<any>();
          inputInfo.inputType = 'CAAS_PREFERENCES';
          inputInfo.prefAccounts = structuredClone(res?.data?.data?.context);
          inputInfo.prefCategories = this.aiUtil?.sortCategories(JSON.parse(JSON.stringify(res?.data?.data?.categoriesList)));
          const chat = this.aiUtil?.setWorkFlowInfo('CAAS_PREFERENCES');
          chat.specialPromptData = structuredClone(inputInfo);
          const currentChat = structuredClone(this.chatHistory());
          currentChat?.messages.pop();
          currentChat?.messages?.push(chat);
          this.chatHistory.set(structuredClone(currentChat));
        } else {
          this.updateErrorState(structuredClone(this.chatHistory()), res?.data?.errors[0]);
        }
        if (res?.data && res?.data?.chat_object?.status === 200 && !!res?.data?.chat_object?.data) {
          const chatList = res?.data?.chat_object?.data ? res?.data?.chat_object?.data : null;
          this.updateChatInformation(chatList);
        }
      },
      error: err => {
        this.updateErrorState(structuredClone(this.chatHistory()));
      }
    }).add(() => this.setQueryProgressState('DONE'));
  }

  public getAmortizationSchedule(data: AmortizationInputsModel[]) {
    const formData: FormData = new FormData();
    formData.append('prompt_type', 'capital_amortization');
    formData.append('data', JSON.stringify({
      engagement_id: this.selectedEng()?.engId,
      clientName: this.selectedEng()?.clientName,
      input_data: {'context': data}
    }));
    const chatObj = this.getChatSavingObj(this.selectedPrompt()?.prompt, 'Fetching the Amortization Schedule Information With user provided inputs');
    formData.append('chat_object', JSON.stringify(chatObj));
    this.setQueryProgressState('PROGRESS');
    const inputInfo: LucaInputsModel<AmortizationScheduleModel[]> = new LucaInputsModel<AmortizationScheduleModel[]>();
    this.chatService.fetchPromptReport(formData).pipe(
      takeUntil(this.cancelRequest$)
    ).subscribe({
      next: res => {
        if (res?.data?.status === 200 && !!(res?.data?.data?.context)) {
          inputInfo.amortizationDetails = this.aiUtil.transformAmorCalcRows(JSON.parse(JSON.stringify(res?.data?.data?.calculation_table))) ?? [];
          inputInfo.generalLedger = res?.data?.data?.generalLedger ?? [];
          inputInfo.scheduleRows = this.aiUtil.transformScheduleWithDetail(res?.data?.data?.amortization_schedule ?? [],
            inputInfo?.amortizationDetails ?? [] , inputInfo?.generalLedger);
          inputInfo.adjustmentEntries = res?.data?.data?.adj_entry ?? [];
          inputInfo.inputType = 'CAAS_SCHEDULE';
          inputInfo.prefAccounts = structuredClone(data);
          inputInfo.year = this.selectedEng()?.yearEnd;
          inputInfo.tbAccounts = res?.data?.data?.tbAccounts ?? [];
          const chat = this.aiUtil?.setWorkFlowInfo('CAAS_SCHEDULE');
          chat.specialPromptData = structuredClone(inputInfo);
          chat.lucaOutputFilePath = res?.data?.data?.file_path;
          const currentChat = structuredClone(this.chatHistory());
          currentChat?.messages.pop();
          currentChat?.messages?.push(chat);
          this.chatHistory.set(structuredClone(currentChat));
          this.filePath.set(res?.data?.data?.file_path);
          this.context.set(res?.data?.data?.context);
        } else {
          this.updateErrorState(structuredClone(this.chatHistory()), res?.data?.errors[0]);
        }
      },
      error: err => {
        this.updateErrorState(structuredClone(this.chatHistory()));
      }
    }).add(() => {
      this.setQueryProgressState('DONE');
      this.updateAmortizationSchedule(structuredClone(inputInfo));
    });
  }

  public saveUserSelectedInputs(data: ChatLucaMsgModel, aiOutput: Partial<LucaInputsModel>, isAmortization: boolean = false) {
    const Obj: SaveChatMsgModel = new SaveChatMsgModel();
    Obj.filePath = this.selectedChat()?.filePath;
    Obj.message = structuredClone(data);
    this.chatService.saveUserSelectedInputs(Obj).pipe().subscribe({
      next: res => {
      }
    }).add(() => {
      if (isAmortization) {
        this.getAmortizationSchedule(aiOutput?.prefAccounts);
      }
    });
  }

  private setQueryProgressState(type: 'PROGRESS' | 'DONE' | 'RESTRICTED') {
    if (type === 'RESTRICTED') {
      if (this.selectedEng()?.engId > 0 && !this.selectedEng()?.isRestricted) {
        this.userInput.enable();
        this.inputImgPath.update(i => {
          if (i.hasOwnProperty('isDisabled')) {
            i.isDisabled = false;
          }
          return i;
        });
        this.cdr.markForCheck();
      } else {
        this.userInput.disable();
        this.inputImgPath.update(i => {
          if (i.hasOwnProperty('isDisabled')) {
            i.isDisabled = true;
          }
          return i;
        });
      }
      ;
      return;
    }
    if (type === 'PROGRESS') {
      this.userInput.setValue('');
      this.userInput.disable({emitEvent: false});
      this.inputImgPath().isDisabled = true;
      this.cancelAndInIt();
    }
    if (type === 'DONE' && !(this.dialogInfo()?.isAiRestricted || this.selectedEng()?.isRestricted)) {
      this.userInput.setValue('');
      this.userInput.enable({emitEvent: true});
      this.attachedFiles()?.length > 0 ? this.inputImgPath.set(structuredClone(VOICE_SEND_INPUT)) : this.inputImgPath.set(structuredClone(VOICE_INPUT));
    }
    this.cdr.markForCheck();
    this.scrollToContent();
  }

  public copyText(index: number) {
    this.hasCopiedText.set(index);
    setTimeout(() => this.hasCopiedText.set(1000), 5000);
  }

  public postEntry(chat: ChatLucaMsgModel) {
    localStorage.removeItem('adjustmentEntrySaved');
    const depriciation: ScheduleAdjustmentModel = chat?.specialPromptData?.adjustmentEntries?.find((r: ScheduleAdjustmentModel) => r?.rowType === 'DEPRECIATION');
    let updatedDep: AdjustmentRowModel = new AdjustmentRowModel();
    if (depriciation) {
      updatedDep.engagementsid = this.selectedEng()?.engId;
      updatedDep.loginid = this.userDetails()?.useracctid;
      updatedDep.accountuuid = depriciation?.accountUUID;
      updatedDep.acctcredit = this.aiUtil?.formatToNumber(depriciation?.credit);
      updatedDep.acctdebit = this.aiUtil?.formatToNumber(depriciation?.debit);
      updatedDep.accountcode = depriciation?.accNo;
      updatedDep.accountname = depriciation?.description;
      updatedDep.trailbalanceid = depriciation?.trialBalId;
    }
    const amortization = chat?.specialPromptData?.adjustmentEntries?.filter((r: ScheduleAdjustmentModel) => r?.rowType === 'AMORTIZATION');
    const total: ScheduleAdjustmentModel = chat?.specialPromptData?.adjustmentEntries?.find((r: ScheduleAdjustmentModel) => r?.rowType === 'TOTAL');
    let updatedRows: AdjustmentRowModel[];
    amortization?.forEach((r: ScheduleAdjustmentModel) => {
      const row = new AdjustmentRowModel();
      row.engagementsid = this.selectedEng()?.engId;
      row.loginid = this.userDetails()?.useracctid;
      row.accountuuid = r?.accountUUID;
      row.acctcredit = this.aiUtil?.formatToNumber(r?.credit);
      row.acctdebit = this.aiUtil?.formatToNumber(r?.debit);
      row.accountcode = r?.accNo;
      row.accountname = r?.description;
      row.trailbalanceid = r?.trialBalId;
      updatedRows?.length > 0 ? updatedRows.push(row) : (updatedRows = [row]);
    });
    const inputObj: AdjustmentObj = new AdjustmentObj();
    inputObj.adjustmenttypeid = 1;
    inputObj.adjustmenttypename = 'Normal';
    inputObj.children = updatedDep ? [updatedDep, ...updatedRows] : [...updatedRows];
    inputObj.entry_date = '2023-12-31';
    inputObj.credittotal = this.aiUtil?.formatToNumber(total?.credit);
    inputObj.debittotal = this.aiUtil?.formatToNumber(total?.debit);
    localStorage.setItem('trialBalSelectedYear', this.selectedEng()?.year);
    localStorage.setItem('trialBalAdjmtObj', JSON.stringify([inputObj]));
    const newWindowUrl = window.location.href.replace(this.loc.path(), '') + '/adj-entry/i?entryOrigin=CAAS';
    window.open(newWindowUrl, '_blank', 'location=yes,height=650,width=885,scrollbars=yes,status=yes,left=600,top=250');
  }

  public openAsWindow() {
    this.SH.setItem(this.AK.aiInfo, structuredClone(this.getClosingState()));
    this.SH.setStringItem(this.AK.aiState, 'WINDOW');
    const newWindowUrl = window.location.href.replace(this.loc.path(), '') + '/ai-bot';
    window.open(newWindowUrl, '_blank', 'location=yes,height=975,width=1910,scrollbars=yes,status=yes,left=0, top=0');
    this.minimizeWindow('CONVERTED');
  }

  downloadAiResponse(Obj: AiDownloadModel) {
    const currentDate = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const fileExtension = this.aiUtil?.getAppropriateExtension(Obj);
    const cleanFileName = `${Obj?.fileName}-${currentDate}${fileExtension}`;
    if (Obj?.hasSystemFile === true && Obj?.exportType !== 'XLSX') {
      this.chatService.downloadSystemFile(Obj).pipe().subscribe({
        next: res => {
          saveAs(res, cleanFileName);
          this.setActionState(this.index, false, 'UPDATE');
        }, error: err => {
        }
      });
    }
    if (Obj?.hasSystemFile === true && Obj?.exportType === 'XLSX') {
      this.download(Obj?.filePath, cleanFileName);
    }
    if (!Obj?.hasSystemFile === true) {
      this.chatService.downloadAiResponse(Obj).pipe().subscribe({
        next: res => {
          saveAs(res, cleanFileName);
          this.setActionState(this.index, false, 'UPDATE');
        }, error: err => {
        }
      });
    }
  }

  download(filePath: string = '', fileName: string = 'Ai-Generated-File') {
    if (!filePath) {
      return;
    }
    let filePathUrl = 'https://s3.amazonaws.com/' + this.userDetails()?.s3BucketName + '/' + filePath;
    const object = {'s3FileDownlaodPath': filePathUrl};
    const convertedObject = JSON.stringify(object);
    this.apiService.fileDownload(convertedObject).subscribe(response => {
      if (response) {
        saveAs(response, fileName);
        this.setActionState(this.index, false, 'UPDATE');
      }
    }, error => {
    });
  }

  private saveToEngagement(Obj: AiDownloadModel) {
    this.chatService.saveToEngagement(Obj).pipe().subscribe({
      next: res => {
        this.setActionState(this.index, true, 'UPDATE');
        if (res?.status === 200 && res?.data) {
          const engInfo = JSON.parse(localStorage.getItem('selectedEngagementObj'));
          if (this.router?.url?.includes('/dashboard/ntrdashboard/') && engInfo?.engagementsid === Obj?.engagementId) {
            this.engService.refreshEngDocuments$.next(true);
          }
        }
      }
    });
  }

  getHistory(engagementId: number = 0) {
    if (engagementId === 0) {
      return;
    }
    this.chatService.fetchChatHistory(engagementId).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: res => {
        if (res?.status === 200 && res?.data?.length) {
          this.chatsList.set([...res?.data]);
        }
      }
    });
  }

  deleteChat(chat: ChatsModel) {
    if (chat?.id === this.selectedChat()?.id) {
      if (this.selectedEng()?.engId > 0) {
        this.newChat(true);
      } else {
        this.chatHistory.set(null);
        this.selectedChat.set(null);
        const chatInfo = this.AiMsg?.setNewChat(this.selectedEng()?.engId, this.selectedEng()?.isRestricted);
        this.chatHistory.set(structuredClone(chatInfo));
        this.userInput.disable({emitEvent: false});
        this.isFetchingChat.set(false);
      }
    }
    this.chatService.deleteChat(chat).subscribe({
      next: res => {
        if (res?.status === 200 && res?.data) {
          this.getHistory(this.selectedEng()?.engId);
        }
      }
    });
  }

  renameChat(chat: ChatsModel) {
    if (!(chat?.id > 0)) {
      return;
    }
    this.chatService.createOrUpdateChat(structuredClone(chat)).subscribe({
      next: res => {
        if (res?.status === 200 && res?.data) {
          this.getHistory(this.selectedEng()?.engId);
        }
      }
    });
  }

  updateStorageValues() {
    if (this.router.url.includes('/ai-bot')) {
      this.isWindowMode.set(true);
    } else {
      this.isWindowMode.set(false);
    }
    this.isInEngagement.set(this.router.url.includes('/dashboard/ntrdashboard/'));
  }

  public setPromptByEngType(engTypeId: number = 0) {
    switch (engTypeId) {
      case 1:
        this.suggestedPrompts.set(PROMPTS?.filter(p => p?.showForEng.includes(1) === true));
        break;
      case 2:
        this.suggestedPrompts.set(PROMPTS?.filter(p => p?.showForEng.includes(2) === true));
        break;
      default:
        this.suggestedPrompts.set(PROMPTS?.filter(p => p?.showForEng.includes(3) === true));
        break;
    }
  }

  public triggerSideBarActions(action: Partial<ChatActionModel>) {
    switch (action?.actionType) {
      case 'START_CHAT':
        this.newChat(action?.performAction);
        break;
      case 'DELETE_CHAT':
        this.deleteChat(action?.chat);
        break;
      case 'SELECT_CHAT':
        this.navigateToHistory(action?.chat);
        break;
      case 'NAVIGATE':
        this.triggerAction.emit({closedType: 'NAVIGATE', route: action?.route});
        break;
      case 'RENAME_CHAT':
        this.renameChat(action?.chat);
        break;
    }
  }

  public closeRepo(res: { isUpload: boolean, selectedFiles: any[] }) {
    this.openDocRepo.set(false);
    if (res && res?.isUpload && res?.selectedFiles?.length > 0) {
      const lucaFiles = this.aiUtil?.convertToLucaFileModel(res?.selectedFiles);
      this.addFilesToChat(lucaFiles);
    }
  }

  private setLoadingState() {
    const aiChat = this.aiUtil?.getAiChatMsg('SYSTEM_PROMPT_RES_LOADING', this.getLoadingContent());
    this.setHistoryByChatMsg(aiChat, 'PUSH');
  }

  private filterMsgInHistory() {
    this.chatHistory.update(h => {
      (h?.messages?.length > 0) && h?.messages?.pop();
      return h;
    });
  }

  private setAttachedFiles(files: File[]) {
    const attachFiles = this.chatService.attachedFiles.getValue() ? this.chatService.attachedFiles.getValue()
      : new Map<string, File>([]);
    files?.forEach(f => {
      attachFiles.set(f?.name, f);
    });
    this.chatService.attachedFiles.next(attachFiles);
  }

  private getPreviousMessages(data: Partial<LucaInputsModel>) {
    const history = structuredClone(this.chatHistory());
    const updatedMsg = history?.messages?.slice(0, 2);
    const aiChat = this.aiUtil?.getAiChatMsg('WORKFLOW');
    aiChat.promptType = data?.inputType;
    aiChat.specialPromptMessage = data?.inputType;
    aiChat.specialPromptData = {
      'investmentConfirmation': data?.hasConfirmation,
      'inputType': data?.inputType,
      'isDisabled': true,
      'attachedFiles': data?.attachedFiles,
      'message': data?.message
    };
    return [...updatedMsg, aiChat];
  }

  public closeContextWindow(info: Partial<freeContextOutputModel>) {
    this.openContextWindow.set(false);
    this.chatHistory.update(chat => {
      chat.freeContext = JSON.parse(JSON.stringify(info?.updatedContext));
      return chat;
    });
    switch (info?.actionType) {
      case 'CLOSE':
        this.contextRes.set(structuredClone(info?.contextRes));
        break;
      case 'REFRESH':
        this.triggerAction.emit({closedType: 'NAVIGATE', route: '/dashboard/ntrdashboard/TrialBalance'});
        break;
      case 'SAVED':
        this.contextRes.set(structuredClone(info?.contextRes));
        break;
      case 'DRAFT':
        this.contextRes.set(structuredClone(info?.contextRes));
        break;
    }
    this.cdr.detectChanges();
  }

  public getContextDetails() {
    this.isContextLoading.set(true);
    this.chatService.getContextDetails(this.selectedEng().engId,
      (this.selectedEng().integrationId ? this.selectedEng().integrationId : 0),
      this.selectedEng()?.year).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (response) => {
        if (response.status === 200 && response.data) {
          this.contextRes.set(response.data);
        }
      }
    }).add(() => this.isContextLoading.set(false))
  }

  public openFreeContext() {
    document.getElementsByClassName('context-right-arrow') &&
    document.getElementsByClassName('context-right-arrow')[0] &&
    document.getElementsByClassName('context-right-arrow')[0].classList.add('d-non');
    this.openContextWindow.set(true);
  }

  public chatListCount(value: number) {
    this.isShownFreeContextArrow.set(value);
  }

  private scrollToContent() {
    setTimeout(() => {
      // this.endOfPage.nativeElement.scrollIntoView({behavior: 'smooth'});
    }, 100);
  }

  public setSearchText(event: any) {
    const ele = event?.target as HTMLInputElement;
    const searchText = ele?.value;
    if (this.selectedEng()?.engId > 0 && (this.selectedEng()?.engName?.toLocaleLowerCase() != searchText?.trim()?.toLocaleLowerCase())) {
      setTimeout(() => {
        this.searchText.setValue(this.selectedEng()?.engName);
        this.searchText.updateValueAndValidity({onlySelf: false, emitEvent: true});
        this.searchEngText.set(this.selectedEng()?.engName);
      }, 300);
    }
  }

  private cancelAndInIt() {
    this.cancelRequest$.next();
    this.cancelRequest$.complete();
    this.cancelRequest$ = new Subject<void>();
  }

  public resetUserInput(event: any) {
    const element = event?.target as HTMLInputElement;
    const userInput = element?.value;
    if (!userInput?.trim()) {
      this.setQueryProgressState('DONE');
    }
  }

  public debouncedScroll(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => this.updateButtonVisibility(), 100);
  }

  private updateButtonVisibility(): void {
    const container = this.mainChatWindowEle.nativeElement;
    const hasScrollableContent = container.scrollHeight > container.clientHeight;
    this.showGoToTop.set(hasScrollableContent && !(container.scrollTop === 0));
  }

  scrollToTop(): void {
    this.mainChatWindowEle.nativeElement.scrollTo({top: 0, behavior: 'smooth'});
  }

  private updateResponseStateForAPrompt(uuid: string) {
    const currentUUID = this.chatService.currentUUID;
    if ((uuid === currentUUID && (this.chatService.progressType === 'PROMPT' || this.chatService.progressType === 'FILE_PROMPT')) &&
      (this.chatService.cachedPromptResponse.has(uuid) && this.chatService.cachedPromptResponse.get(uuid))) {
      const response = this.chatService.cachedPromptResponse.get(uuid);
      if (response?.data?.status === 200 && (!!(response?.data?.data?.context) || !!(response?.data?.data?.markdown?.string))) {
        this.updateSuccessState(JSON.parse(JSON.stringify(this.chatHistory())), response?.data?.data,
          this.chatService.progressType === 'FILE_PROMPT', this.chatService.progressType === 'FILE_PROMPT');
      } else {
        this.updateErrorState(JSON.parse(JSON.stringify(this.chatHistory())), response?.data?.errors[0]);
      }
      if (response?.data && response?.data?.chat_object?.status === 200 && !!response?.data?.chat_object?.data) {
        const chatList = response?.data?.chat_object?.data ? response?.data?.chat_object?.data : null;
        this.updateChatInformation(chatList);
      }
      setTimeout(() => {
        this.chatService.resetValues();
      }, 1000);
    }
    if ((uuid === currentUUID && this.chatService.progressType === 'FREE') &&
      (this.chatService.cachedChatResponse.has(uuid) && this.chatService.cachedChatResponse.get(uuid))) {
      const response = this.chatService.cachedChatResponse.get(uuid);
      if (response) {
        this.setHistoryByChatMsg(this.aiUtil?.getAiChatMsg('SYSTEM_PROMPT_RES_OUTPUT', response?.response), 'REMOVE');
      }
      if (response && response?.chat_object?.status === 200 && !!response?.chat_object?.data) {
        const chatList = response?.chat_object?.data ? response?.chat_object?.data : null;
        this.updateChatInformation(chatList);
      }
      setTimeout(() => {
        this.chatService.resetValues();
      }, 1000);
    }

  }

  private getPromptContext() {
    const promptId = this.aiUtil?.getPromptId(this.selectedPrompt()?.id);
    switch (promptId) {
      case 1:
        return '';
      case 2:
        return (this.context()?.detail && this.context()?.summary_context) ?`Take the ${JSON.stringify(this.context()?.detail)} as
        Aged AR Details Information along with ${JSON.stringify(this.context()?.summary_context)}
        as Aged AR summary and provide accurate information with following query` : null;
      case 3:
        const glRows = this.chatHistory()?.messages?.find(m => m?.messageType === "WORKFLOW" &&
          m?.specialPromptMessage === "GENERAL_LEDGER")?.specialPromptData?.glSelection?.selectedRows
        return  glRows?.length > 0 ? `Take the ${JSON.stringify(glRows)} GL data
         as Context and Provide the Information for the Following Query`: null;
      case 4:
        return '';
      case 6:
        return '';
      case 8:
        return this.chatHistory()?.context ? `Take The Context information: ${this.chatHistory()?.context} and provide the information for the following query` : null;
    }
  }

  public updateStreamingInfo(index: number, isStreamingDone: boolean = false) {
    if (isStreamingDone && ((index + 1) === this.chatHistory()?.messages?.length)) {
      this.chatHistory.update((chat) => {
        if (chat?.messages[index]) {
          chat.messages[index].isStreamingCompleted = true;
        }
        return chat;
      });
    }
  }

  ngOnDestroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  private initializeSpeechRecognition() {
    this.isVoiceChatDisabled.set(
      this.aiUtil.isFirefox()
  );
}

public removeFile(index: number,file: LucaFileModel) {
  this.attachedFiles.update(files => {
    return files.filter((f, i) => i != index);
  });
  if(file?.fileOrigin === 'UPLOAD') {
    this.uploadedFiles = this.uploadedFiles?.filter(f => f?.name !== file?.fileName);
    }
  }

  public toggleContext() {
    if( this.openContextWindow()){
      this.openContextWindow.set(false);
    } else {
      this.openFreeContext()
    }
  }

  public onResizePanelEnd(resizeData: ResizeModel) {
    resizeData.width < resizeData.minWidth ? this.drawer.close() : '';
  }

  public getTbAccounts(inputType: PROMPT_MSG_TYPES) {
    this.setQueryProgressState('PROGRESS');
    forkJoin([
      this.chatService.fetchTbAccounts(this.selectedEng()?.engId, +this.selectedEng()?.year),
      this.plaidService.plaidStatus(this.selectedEng()?.clientId, this.selectedEng()?.engId)
    ])
      .pipe(
      takeUntilDestroyed(this.destroyRef),
        switchMap(([tbAccountsRes, plaidStatusRes]) => {
          const plaidStatus = plaidStatusRes?.data;
          const shouldFetchAccounts = (plaidStatusRes?.status === 200 && plaidStatus?.requestSent && plaidStatus?.connected);
          if (shouldFetchAccounts) {
            return this.plaidService.fetchAccounts(plaidStatus.clientKey).pipe(
              map(fetchAccountsRes => ({
                tbAccountsRes,
                fetchAccountsRes
              }))
            );
          }
          return of({
            tbAccountsRes,
            fetchAccountsRes: null
          });
        })
    ).subscribe({
      next: ({tbAccountsRes, fetchAccountsRes}) => {
        if (tbAccountsRes?.status === 200 && Array.isArray(tbAccountsRes?.data) && tbAccountsRes.data.length > 0) {
          const inputInfo: LucaInputsModel = new LucaInputsModel();
          inputInfo.inputType = inputType;
          inputInfo.reconInfo = {
            tbAccounts: this.aiUtil.formatTbRows(tbAccountsRes?.data),
            plaidInstitutions: fetchAccountsRes?.data,
            hasPlaid: fetchAccountsRes?.data?.length > 0
          };
          inputInfo.message = (fetchAccountsRes?.data?.length > 0 ?
            this.aiResponses?.reconPlaid : this.aiResponses?.reconTb);
          this.reconInfo.set({...inputInfo.reconInfo});
          const chat = this.aiUtil?.setWorkFlowInfo(inputType);
          chat.specialPromptData = structuredClone(inputInfo);
          const currentChat = structuredClone(this.chatHistory());
          currentChat?.messages.pop();
          currentChat?.messages?.push(chat);
          this.chatHistory.set(structuredClone(currentChat));
        }
      },
      error: err => {
      }
    }).add(() => this.setQueryProgressState('DONE'))
  }

  private getRecon(formData: FormData, chat: SavedChatHistoryModel) {
    this.setQueryProgressState('PROGRESS');
    this.chatService.fetchReconciliation(formData).subscribe({
      next: res => {
        if (res?.data?.status === 200 && (!!(res?.data?.data?.context) || !!(res?.data?.data?.markdown?.string))) {
          this.updateSuccessState(chat, res?.data?.data, false, true);
        } else {
          this.updateErrorState(chat, res?.data?.errors[0]);
        }
        if (res?.data && res?.data?.chat_object?.status === 200 && !!res?.data?.chat_object?.data) {
          const chatList = res?.data?.chat_object?.data ? res?.data?.chat_object?.data : null;
          this.updateChatInformation(chatList);
        }
      }
    }).add(() => this.setQueryProgressState('PROGRESS'));
  }

}
